import { LifeBuoy, Mail, Copy, Check, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAuth } from '@/contexts/auth-context';

const SUPPORT_EMAIL = 'hr@paragons.services';
// Tab-scoped: a page that turns out to overlap this button can be tucked
// away for the rest of the visit without losing it for the next one.
const MINIMIZED_KEY = 'support-contact-minimized';

export default function SupportContactButton() {
  const [copied, setCopied] = useState(false);
  const [minimized, setMinimized] = useState(
    () => sessionStorage.getItem(MINIMIZED_KEY) === '1',
  );
  const { user } = useAuth();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(SUPPORT_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const minimize = () => {
    sessionStorage.setItem(MINIMIZED_KEY, '1');
    setMinimized(true);
  };

  const restore = () => {
    sessionStorage.removeItem(MINIMIZED_KEY);
    setMinimized(false);
  };

  // Recruiters/HR are already the team behind this address - only
  // candidates and signed-out visitors need a way to reach them.
  const isRecruiter = !!user && user.data.role !== 'candidate';
  if (isRecruiter) return null;

  if (minimized) {
    return (
      <button
        type="button"
        onClick={restore}
        aria-label="Show help button"
        className="fixed bottom-5 right-0 z-50 flex h-9 w-5 items-center justify-center rounded-l-full border border-r-0 border-white/10 bg-primary text-primary-foreground shadow-lg"
      >
        <LifeBuoy className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="relative">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="h-12 rounded-full px-5 shadow-lg"
              aria-label="Contact support"
            >
              <LifeBuoy className="h-5 w-5" />
              Need Help?
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" side="top" className="w-80">
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-foreground">Need help?</p>
                <p className="text-sm text-muted-foreground">
                  Facing an issue or have a question? Reach out and we'll get
                  back to you.
                </p>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
                <span className="truncate text-sm font-medium text-foreground">
                  {SUPPORT_EMAIL}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                  onClick={handleCopy}
                  aria-label="Copy support email address"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button asChild className="w-full">
                <a href={`mailto:${SUPPORT_EMAIL}`}>
                  <Mail />
                  Email us
                </a>
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={minimize}
          aria-label="Hide help button"
          className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-background text-muted-foreground shadow hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
